/**
 * Landing-page bug-report + feedback form. Pure DOM, no WebGL — same standing
 * as the rest of `ui/landing/`.
 *
 * Submits to the first-party Pages Function at `/api/feedback` (see
 * `functions/api/feedback.ts`), which persists to D1. The report carries the
 * device context the game already knows pre-boot (graphics tier, touch,
 * viewport) so a "it lags on my machine" report arrives with the machine
 * attached. No name field is stored alongside the message — contact is
 * optional and free-text.
 */
import type { QualityTier } from '../../engine/Quality';

export type FeedbackKind = 'bug' | 'feedback';

const ENDPOINT = '/api/feedback';
const MAX_MESSAGE = 2000;
const MAX_CONTACT = 120;

export interface FeedbackContext {
  readonly tier: QualityTier;
  readonly touch: boolean;
}

export function buildFeedback(ctx: FeedbackContext): { el: HTMLElement } {
  const form = el('form', 'ls-feedback-form') as HTMLFormElement;
  form.setAttribute('aria-label', 'Report a bug or send feedback');
  form.noValidate = true;

  const kindRow = el('div', 'ls-feedback-row');
  const kindLabel = el('label', 'ls-feedback-label', 'I want to') as HTMLLabelElement;
  kindLabel.htmlFor = 'ls-feedback-kind';
  const kind = el('select', 'ls-feedback-kind') as HTMLSelectElement;
  kind.id = 'ls-feedback-kind';
  kind.name = 'kind';
  for (const [value, label] of [
    ['bug', 'Report a bug'],
    ['feedback', 'Send feedback'],
  ] as [FeedbackKind, string][]) {
    const opt = document.createElement('option');
    opt.value = value;
    opt.textContent = label;
    kind.appendChild(opt);
  }
  kindRow.append(kindLabel, kind);

  const msgLabel = el('label', 'ls-feedback-label', 'What happened?') as HTMLLabelElement;
  msgLabel.htmlFor = 'ls-feedback-message';
  const message = el('textarea', 'ls-feedback-message') as HTMLTextAreaElement;
  message.id = 'ls-feedback-message';
  message.name = 'message';
  message.rows = 5;
  message.maxLength = MAX_MESSAGE;
  message.required = true;
  message.placeholder =
    'What were you doing, what did you expect, what happened instead? (build, browser and device info is attached automatically)';
  message.setAttribute('aria-describedby', 'ls-feedback-status');

  const contactLabel = el('label', 'ls-feedback-label', 'Contact (optional)') as HTMLLabelElement;
  contactLabel.htmlFor = 'ls-feedback-contact';
  const contact = el('input', 'ls-feedback-contact') as HTMLInputElement;
  contact.id = 'ls-feedback-contact';
  contact.name = 'contact';
  contact.type = 'text';
  contact.maxLength = MAX_CONTACT;
  contact.placeholder = 'Email or Discord — only if you want a reply';
  contact.autocomplete = 'email';

  const actions = el('div', 'ls-feedback-actions');
  const submit = el('button', 'ls-feedback-submit', 'Send report') as HTMLButtonElement;
  submit.type = 'submit';
  actions.appendChild(submit);

  const status = el('p', 'ls-feedback-status') as HTMLParagraphElement;
  status.id = 'ls-feedback-status';
  status.setAttribute('role', 'status');
  status.hidden = true;

  form.append(kindRow, msgLabel, message, contactLabel, contact, actions, status);

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    if (submit.disabled) return;
    const text = message.value.trim();
    if (!text) {
      setStatus(status, 'Please describe the bug or feedback first.', false);
      message.focus();
      return;
    }
    void send(status, submit, {
      kind: kind.value === 'feedback' ? 'feedback' : 'bug',
      message: text.slice(0, MAX_MESSAGE),
      contact: contact.value.trim().slice(0, MAX_CONTACT) || undefined,
      tier: ctx.tier,
      mobile: ctx.touch,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
    });
  });

  return { el: form };
}

async function send(
  status: HTMLParagraphElement,
  submit: HTMLButtonElement,
  body: Record<string, unknown>,
): Promise<void> {
  submit.disabled = true;
  submit.textContent = 'Sending…';
  setStatus(status, '', true);
  status.hidden = true;
  try {
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as { ok?: boolean };
    if (!data.ok) throw new Error('rejected');
    submit.textContent = 'Sent — thank you';
    setStatus(status, 'Received. Thank you — reports like this decide what gets fixed next.', true);
  } catch {
    submit.disabled = false;
    submit.textContent = 'Retry send';
    setStatus(
      status,
      'Could not send — check your connection and try again.',
      false,
    );
  }
}

function setStatus(status: HTMLParagraphElement, text: string, ok: boolean): void {
  status.hidden = text === '';
  status.textContent = text;
  status.classList.toggle('error', !ok && text !== '');
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  n.className = className;
  if (text != null) n.textContent = text;
  return n;
}
